"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionEyebrow } from "@/components/marketing/SectionEyebrow";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type FieldErrors = Partial<Record<"firstName" | "lastName" | "email" | "message", string>>;
interface ContactFormProps {
  /** Optional eyebrow [01/N] label rendered above the form. */
  eyebrow?: {
    index: number;
    total: number;
    label: string;
  };
  /** Optional heading rendered above the form. */
  heading?: string;
  /** Optional supporting text rendered below the heading. */
  description?: string;
}
export function ContactForm({
  eyebrow,
  heading = "Get in touch",
  description = "We'd love to hear from you. Please fill out this form."
}: ContactFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!lastName.trim()) next.lastName = "Last name is required.";
    if (!email.trim()) {
      next.email = "Email is required.";
    } else if (!EMAIL_REGEX.test(email.trim())) {
      next.email = "Please enter a valid email address.";
    }
    if (!message.trim()) next.message = "Please add a message.";
    return next;
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    // Clear the previous "thanks" message so it doesn't sit alongside
    // a fresh in-flight submission when the user sends a second message.
    setSubmitted(false);
    setSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim()
        })
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(data.error ?? "Failed to send message. Please try again.");
      }
      setSubmitted(true);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setMessage("");
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (error) {
      const text = error instanceof Error ? error.message : "Failed to send message. Please try again.";
      toast.error(text);
    } finally {
      setSubmitting(false);
    }
  };
  return <section className="px-6 py-16 md:py-24">
      <div className="mx-auto max-w-[1280px]">
        <div className="mx-auto flex max-w-[640px] flex-col items-center text-center">
          {eyebrow ? <SectionEyebrow {...eyebrow} className="mb-3" /> : null}
          <h2 className="text-ds-text-primary mb-4 text-3xl font-medium tracking-tight md:text-5xl">{heading}</h2>
          <p className="text-ds-text-muted max-w-xl text-base md:text-lg">{description}</p>
        </div>

        <form onSubmit={handleSubmit} noValidate aria-busy={submitting} className="mx-auto mt-10 flex w-full max-w-[480px] flex-col gap-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FieldGroup htmlFor="contact-first-name" label="First name" required error={errors.firstName}>
              <Input id="contact-first-name" name="firstName" autoComplete="given-name" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} required aria-invalid={Boolean(errors.firstName)} />
            </FieldGroup>

            <FieldGroup htmlFor="contact-last-name" label="Last name" required error={errors.lastName}>
              <Input id="contact-last-name" name="lastName" autoComplete="family-name" placeholder="Last name" value={lastName} onChange={e_0 => setLastName(e_0.target.value)} required aria-invalid={Boolean(errors.lastName)} />
            </FieldGroup>
          </div>

          <FieldGroup htmlFor="contact-email" label="Email" required error={errors.email}>
            <Input id="contact-email" name="email" type="email" inputMode="email" autoComplete="email" placeholder="you@company.com" value={email} onChange={e_1 => setEmail(e_1.target.value)} required aria-invalid={Boolean(errors.email)} />
          </FieldGroup>

          <FieldGroup htmlFor="contact-phone" label="Phone number">
            <Input id="contact-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={e_2 => setPhone(e_2.target.value)} />
          </FieldGroup>

          <FieldGroup htmlFor="contact-message" label="Message" required error={errors.message}>
            <Textarea id="contact-message" name="message" placeholder="Leave us a message..." rows={6} value={message} onChange={e_3 => setMessage(e_3.target.value)} required aria-invalid={Boolean(errors.message)} className="min-h-[140px] resize-y" />
          </FieldGroup>

          <Button type="submit" variant="brand" size="lg" disabled={submitting} className="w-full">
            {submitting ? "Sending..." : "Send message"}
          </Button>

          {submitted ? <p role="status" aria-live="polite" className="text-ds-text-muted text-center text-sm">
              Thanks — your message is on its way. We'll be in touch shortly.
            </p> : null}
        </form>
      </div>
    </section>;
}
function FieldGroup(t0) {
  const $ = _c(12);
  const {
    htmlFor,
    label,
    required,
    error,
    children
  } = t0;
  let t1;
  if ($[0] !== required) {
    t1 = required ? <span className="text-ds-brand-600 ml-0.5">*</span> : null;
    $[0] = required;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== htmlFor || $[3] !== label || $[4] !== t1) {
    t2 = <Label htmlFor={htmlFor} className="text-ds-text-primary">{label}{t1}</Label>;
    $[2] = htmlFor;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== error) {
    t3 = error ? <p className="text-ds-error-text text-xs" role="alert">{error}</p> : null;
    $[6] = error;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex flex-col gap-1.5">{t2}{children}{t3}</div>;
    $[8] = children;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}